import React, { useState } from 'react';
import {
  Button,
  Drawer,
  Typography,
  Divider,
  TextField,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
// Styles
import { Close } from '@mui/icons-material';
import { Field, Form, Formik, ErrorMessage, FieldProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserList, updateUserAction } from './actions';
import { LoadingButton } from '@mui/lab';
import { Watch } from 'react-loader-spinner';
import * as Yup from 'yup';
import { setErrorToastState } from 'Store/action';
import { set_edit_user_drawer } from 'Store/reducers/adminReducer';
import { set_users, set_user_details } from 'Store/reducers/userReducer';
import { AppDispatch, RootState } from 'Store/Store';
import { EMAIL_REGEX } from 'shared/constants/regex';

export const EditUser: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const edit_user_drawer = useSelector((state: RootState) => state?.admin?.edit_user_drawer);
  const user_details = useSelector((state: RootState) => state?.user?.user_details);
  const [loading, setLoading] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const updateUser = (data: any) => {
    setServerErrorMessage('');
    setLoading(true);

    if (user_details) {
      updateUserAction(user_details?.id, data)
        .then(res => {
          if (res.status === 200) {
            fetchUserList().then(res => {
              if (res.status === 200) {
                dispatch(set_users(res.data));
              }
            });
            dispatch(set_edit_user_drawer(false));
            dispatch(set_user_details(res.data));
          }
          setLoading(false);
        })
        .catch(err => {
          setLoading(false);
          dispatch(setErrorToastState(true, 'Something went wrong, please try again in a while.'));
        });
    }
  };

  return (
    <Drawer
      BackdropProps={{ invisible: true }}
      anchor="right"
      open={edit_user_drawer}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}>
      <Formik
        validationSchema={Yup.object().shape({
          first_name: Yup.string().max(255).required('Required!'),
          last_name: Yup.string().max(255).required('Required!'),
          email: Yup.string().max(255).email('Invalid email').matches(EMAIL_REGEX, 'Invalid email format').required('Required'),    
          phone: Yup.string().max(255),
          password: Yup.string().matches(
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
            'Password must contain at least 8 characters, one uppercase, one number and one special case character',
          ),
          role: Yup.string().min(1).required('Required!'),
        })}
        onSubmit={updateUser}
        enableReinitialize
        initialValues={{
          first_name: user_details?.first_name || '',
          last_name: user_details?.last_name || '',
          email: user_details?.email || '',
          phone: user_details?.phone || '',
          password: user_details?.password || '',
          role: user_details?.role?.name || '',
        }}>
        {({ touched, errors, handleSubmit }) => (
          <Form style={{ height: '100%' }}>
            <Grid
              container
              direction="column"
              flexWrap="nowrap"
              justifyContent="space-between"
              spacing={4}>
              <Grid container direction="column" spacing={4}>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                  <Grid>
                    <Typography variant="h4" sx={{ color: '#FFF' }}>
                      Update User
                    </Typography>
                  </Grid>
                  <Grid>
                    <IconButton
                      disabled={loading}
                      onClick={() => dispatch(set_edit_user_drawer(false))}>
                      <Close />
                    </IconButton>
                  </Grid>
                </Grid>
                <Grid>
                  <Typography variant="h6">User account</Typography>
                </Grid>
                <Grid container direction="column" spacing={1}>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                      container
                      spacing={1}
                      direction="column">
                      <Grid>
                        <Typography variant="h5" sx={{ color: '#FFF' }}>
                          First name
                        </Typography>
                      </Grid>
                      <Grid>
                        <Field name="first_name">
                          {({ field }: FieldProps) => (
                            <TextField
                              fullWidth
                              {...field}
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid>
                        <ErrorMessage name="first_name">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                      container
                      spacing={1}
                      direction="column">
                      <Grid>
                        <Typography variant="h5" sx={{ color: '#FFF' }}>
                          Last name
                        </Typography>
                      </Grid>
                      <Grid>
                        <Field name="last_name">
                          {({ field }: FieldProps) => (
                            <TextField
                              fullWidth
                              {...field}
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid>
                        <ErrorMessage name="last_name">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5" sx={{ color: '#FFF' }}>
                        Email
                      </Typography>
                    </Grid>
                    <Grid>
                      <Field name="email">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            {...field}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="email">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5" sx={{ color: '#FFF' }}>
                        Phone Number
                      </Typography>
                    </Grid>
                    <Grid>
                      <Field name="phone">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            placeholder="+44 (0) 7123 456 789"
                            {...field}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="phone">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5" sx={{ color: '#FFF' }}>
                        Password
                      </Typography>
                    </Grid>
                    <Grid>
                      <Field name="password">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            {...field}
                            type="password"
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="password">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <Divider color="#FFF" />
                </Grid>
                <Grid>
                  <Typography variant="h6">User account</Typography>
                </Grid>
                <Grid container direction="column" spacing={1}>
                  <Grid>
                    <Typography variant="h5" sx={{ color: '#FFF' }}>
                      Role
                    </Typography>
                  </Grid>
                  <Grid>
                    <Field name="role">
                      {({ field }: FieldProps) => (
                        <Select
                          sx={{
                            backgroundColor: '#FFFFFF',
                            color: '#000',
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: '#FFFFFF',
                              },
                            },
                            MenuListProps: {
                              sx: {
                                bgcolor: '#FFFFFF',
                                '& .MuiMenuItem-root': {
                                  color: '#000',
                                  '&:hover': {
                                    bgcolor: '#F5F5F5',
                                  },
                                  '&.Mui-selected': {
                                    bgcolor: '#E0E0E0',
                                    color: '#000',
                                  },
                                  '&.Mui-selected:hover': {
                                    bgcolor: '#D3D3D3',
                                  },
                                },
                              },
                            },
                          }}
                          disableUnderline
                          {...field}
                          fullWidth
                          value={field.value}
                          variant="standard"
                          error={touched.role && !!errors.role ? true : false}>
                          <MenuItem value="" selected disabled>
                            Select Role
                          </MenuItem>
                          <MenuItem value="super_admin">Super Admin</MenuItem>
                          <MenuItem value="client">Client</MenuItem>
                        </Select>
                      )}
                    </Field>
                  </Grid>
                  <Grid>
                    <ErrorMessage name="role">
                      {message => <Typography color="error">{message}</Typography>}
                    </ErrorMessage>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container direction="column" spacing={2} mb={4}>
                {serverErrorMessage && (
                  <Grid>
                    <Typography color="error" textAlign="center">
                      {serverErrorMessage}
                    </Typography>
                  </Grid>
                )}
                <Grid>
                  <LoadingButton
                    onClick={() => handleSubmit()}
                    loadingIndicator={<Watch color="#FFF" width={30} height={20} />}
                    loading={loading}
                    variant="contained"
                    sx={{
                      background: '#FFF',
                      color: '#326295',
                    }}
                    fullWidth>
                    Update User
                  </LoadingButton>
                </Grid>
                <Grid>
                  <Button
                    disabled={loading}
                    variant="outlinedWhite"
                    onClick={() => dispatch(set_edit_user_drawer(false))}
                    sx={{
                      border: '1px solid #FFF',
                      color: '#FFF',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.20)',
                        border: '1.5px solid #fff',
                        color: '#fff',
                      },
                    }}
                    fullWidth>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default EditUser;
